import React, { useState } from "react";
import {
	CheckCircle2,
	Circle,
	CircleX,
	ChevronDown,
	ChevronRight,
} from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { QuickstartTask as QuickstartTaskType } from "../quickstart-tasks";
import {
	QuickStartAttributeValue,
	useAttributesStore,
} from "@/dentlab/src/store/Attributes";

interface QuickstartTaskProps {
	task: QuickstartTaskType;
	status: QuickStartAttributeValue;
	onCtaClick?: (to: string) => void;
}

export const QuickstartTask: React.FC<QuickstartTaskProps> = ({
	task,
	status,
	onCtaClick,
}) => {
	const setQuickstartAttribute = useAttributesStore(
		(state) => state.setQuickstartAttribute
	);

	const [isExpanded, setIsExpanded] = useState(false);

	const handleClick = () => {
		setIsExpanded(!isExpanded);
	};

	const handleSkip = () => {
		setQuickstartAttribute(task.id, "skipped");
	};
	const handleUnskip = () => {
		setQuickstartAttribute(task.id, "open");
	};
	const handleResetFromCompleted = () => {
		setQuickstartAttribute(task.id, "open");
	};

	const isCompleted = status === "completed";
	const isSkipped = status === "skipped";

	return (
		<div className="border-b border-gray-200 last:border-b-0">
			<div
				className={cn(
					"flex items-start gap-3 p-4 transition-colors cursor-pointer hover:bg-gray-50"
				)}
				onClick={handleClick}
				data-testid={`quickstart-task-${task.id}`}
			>
				<div className="flex-shrink-0 mt-0.5">
					{isCompleted ? (
						<CheckCircle2 className="w-5 h-5 text-green-600" />
					) : isSkipped ? (
						<CircleX className="w-5 h-5 text-gray-400" />
					) : (
						<Circle className="w-5 h-5 text-gray-400" />
					)}
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center justify-between">
						<h4
							className={cn(
								"text-sm font-medium",
								(isCompleted || isSkipped) &&
									"text-gray-500 line-through",
								!(isCompleted || isSkipped) && "text-gray-900"
							)}
						>
							{task.title}
						</h4>
						<div className="flex-shrink-0 ml-2">
							{isExpanded ? (
								<ChevronDown className="w-4 h-4 text-gray-400" />
							) : (
								<ChevronRight className="w-4 h-4 text-gray-400" />
							)}
						</div>
					</div>
					{isExpanded && (
						<div className="mt-3 text-sm text-gray-600">
							{task.content}
							<div className="mt-3 flex gap-2">
								{task.cta && !isCompleted && !isSkipped && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onCtaClick?.(task.cta!.to);
										}}
										className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
										aria-label={task.cta.label}
										data-testid={`cta-task-${task.id}`}
									>
										{task.cta.label}
									</button>
								)}
								{isSkipped && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleUnskip();
										}}
										className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
										data-testid={`skip-task-${task.id}`}
									>
										Nicht überspringen
									</button>
								)}
								{!isSkipped && !isCompleted && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleSkip();
										}}
										className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
										data-testid={`skip-task-${task.id}`}
									>
										Überspringen
									</button>
								)}
								{isCompleted && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleResetFromCompleted();
										}}
										className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
										data-testid={`complete-task-${task.id}`}
									>
										Zurücksetzen
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
